// src/pages/ConfirmMetadataPage.js
/**
 * ConfirmMetadataPage (ReviewMetadataPage)
 * ---------------------------------------
 * If the user's demographics were updated more than RECERT_MONTHS ago, show a
 * confirmation prompt ("Have any details changed?").
 *
 * Gate logic:
 *  1) Fetch newest metadata timestamp (no JSON download) via ensureProfileInfo().
 *  2) If no metadata exists -> first-time user -> go to /form
 *  3) If newest <= RECERT_MONTHS -> skip confirm -> go to requested recording
 *  4) Otherwise fetch JSON (ensureProfile with force) and render confirm UI.
 */

import React, { useEffect, useMemo, useState, memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ReviewStep from "../components/form/ReviewStep";
import Spinner from "../components/Spinner";

import { questions } from "../utils/form/formQuestions";
import { filterQuestions } from "../utils/form/formRuntime";
import { useCurrentUserClinicMapping } from "../context/UserClinicMappingContext";
import { useProfilePrefetch } from "../context/ProfilePrefetchContext";
import { useAuth } from "../context/AuthContext";
import { useRegionalBackend } from "../context/RegionalBackendContext";
import { RECERT_MS, TTL_MS } from "../utils/constants";
import { trackEvent } from "../utils/analytics/track";
import { getSafeInternalNextPath } from "../utils/navigation/recordingModeNav";
import { buildFormPath } from "../utils/navigation/onboardingPaths";
import { mergeProfileWithClinicMapping } from "../utils/recordingMode/mergeProfileWithClinicMapping";
import { useDelayedVisibility } from "../hooks/useDelayedVisibility";

function ReviewMetadataPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const regionalBackend = useRegionalBackend();
  const mapping = useCurrentUserClinicMapping();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = getSafeInternalNextPath(searchParams.get("next"), "/home");
  const effectiveUser = useMemo(
    () => ({
      ...user,
      returning:
        regionalBackend.profileSummary?.returning ?? user?.returning ?? false,
    }),
    [regionalBackend.profileSummary?.returning, user],
  );

  const [formData, setFormData] = useState(null); // null = loading
  const [filteredQs, setFiltered] = useState([]);
  const showLoadingMessage = useDelayedVisibility(
    !mapping.resolved || formData === null,
  );

  const {
    getCachedProfile,
    ensureProfile,
    getCachedProfileInfo,
    ensureProfileInfo,
  } = useProfilePrefetch();
  const formPath = buildFormPath({
    next: nextPath !== "/home" ? nextPath : null,
  });

  useEffect(() => {
    if (!user?.uid) return;
    if (!mapping.resolved) return;

    let alive = true;

    (async () => {
      try {
        trackEvent("confirm_metadata_gate_start");

        // 1) Fast path: newest timestamp only (no JSON download)
        const cachedInfo = getCachedProfileInfo(user.uid);
        let info = cachedInfo?.status === "ready" ? cachedInfo : null;

        if (!info) {
          info = await ensureProfileInfo({ uid: user.uid, ttlMs: TTL_MS });
        }

        if (!alive) return;

        // No metadata -> first-time user
        if (!info?.latestMs) {
          trackEvent("confirm_metadata_gate_outcome", {
            outcome: "no_metadata",
          });
          navigate(formPath, {
            replace: true,
            state: { returnTo: nextPath },
          });
          return;
        }

        // Up-to-date -> skip confirmation
        const ageMs = Date.now() - info.latestMs;
        if (ageMs <= RECERT_MS) {
          trackEvent("confirm_metadata_gate_outcome", {
            outcome: "up_to_date",
          });
          navigate(nextPath, { replace: true });
          return;
        }

        // 2) Only now: fetch JSON and render confirm screen
        const cached = getCachedProfile(user.uid);
        let data = cached?.status === "ready" ? cached.data : null;

        if (!data) {
          data = await ensureProfile({
            uid: user.uid,
            ttlMs: TTL_MS,
            force: true, // ensure we pull newest when showing the confirm UI
          });
        }

        if (!alive) return;

        if (!data) {
          trackEvent("confirm_metadata_gate_outcome", {
            outcome: "missing_profile_json",
          });
          navigate(formPath, {
            replace: true,
            state: { returnTo: nextPath },
          });
          return;
        }

        const merged = mergeProfileWithClinicMapping(data, mapping);

        setFormData(merged);
        setFiltered(filterQuestions(questions, merged, effectiveUser));
        trackEvent("confirm_metadata_view");
      } catch (err) {
        console.error("Could not evaluate demographic recert gate:", err);
        trackEvent("confirm_metadata_gate_outcome", { outcome: "error" });
        navigate(formPath, {
          replace: true,
          state: { returnTo: nextPath },
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    effectiveUser,
    user?.uid,
    mapping,
    navigate,
    getCachedProfile,
    ensureProfile,
    getCachedProfileInfo,
    ensureProfileInfo,
    nextPath,
    formPath,
  ]);

  if (!mapping.resolved || formData === null) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-3">
        <Spinner />
        {showLoadingMessage ? (
          <p>{t("regionalBackend.loading.confirmMetadata")}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg h-full p-4 flex flex-col justify-start items-center gap-6">
      <ReviewStep
        title={t("confirmMetadata.title")}
        questions={filteredQs}
        formData={formData}
        translationForm="form"
      />

      <div className="flex w-full justify-between pt-4">
        <button
          type="button"
          className="outlined-button"
          onClick={() => {
            trackEvent("confirm_metadata_click_yes");
            navigate(formPath, {
              state: { prefill: formData, returnTo: nextPath },
            });
          }}
        >
          {t("confirmMetadata.yes")}
        </button>

        <button
          type="button"
          className="primary-button"
          onClick={() => {
            trackEvent("confirm_metadata_click_no");
            navigate(nextPath);
          }}
        >
          {t("confirmMetadata.no")}
        </button>
      </div>
    </div>
  );
}

export default memo(ReviewMetadataPage);
